/**
 * CAOS-1583 — the workspace's calls to the two device-approval routes.
 *
 * Two routes, both authenticated, both answering about a grant the person is holding a code for:
 * `auth/device-pending` describes it, `auth/device-approve` answers it. Kept in their own module
 * rather than added to `metadata-source.ts` because they are not metadata and share none of its
 * shapes — and because the approval screen is the only caller either will ever have.
 *
 * ## THE REFUSALS ARE THE INTERESTING PART
 *
 * These two routes refuse far more often than they succeed — a mistyped code, an expired request,
 * one somebody already answered — and every one of those refusals is a sentence a person has to be
 * able to act on. So this module maps the kernel's catalog codes onto the screen's three problem
 * states and NEVER onto a generic failure. A caught exception that collapsed `expired` into
 * "something went wrong" would leave the person re-typing a correct code at a dead request.
 *
 * The codes are the contract, not the messages: the kernel's catalog renders localizable prose from
 * the code, and the code is the stable half (`kernel/src/errors/catalog.ts`). Matching on prose
 * would break the first time somebody reworded a template or a person used the product in another
 * language.
 */

/** The three states the approval screen can render instead of a grant. */
export type ApprovalProblem = 'unknown' | 'expired' | 'already_answered';

/** What the kernel says about a grant awaiting an answer. */
export interface PendingGrantPayload {
  readonly clientName: string;
  readonly requestedAt: string;
  readonly expiresAt: string;
}

export const DEVICE_PENDING_PATH = '/api/auth/device-pending';
export const DEVICE_APPROVE_PATH = '/api/auth/device-approve';

/**
 * The kernel catalog code → screen problem map.
 *
 * `not_found.device_authorization` covers a mistyped code, a code belonging to another org, and a
 * code already exchanged for a credential. The kernel answers all three identically on purpose —
 * distinguishing them would confirm to somebody replaying a leaked code that it had been real — and
 * this map must not try to recover the distinction the kernel deliberately withheld.
 */
const PROBLEM_BY_CODE: Record<string, ApprovalProblem> = {
  'not_found.device_authorization': 'unknown',
  'conflict.device_authorization_expired': 'expired',
  'conflict.device_authorization_already_answered': 'already_answered',
};

/**
 * Read the catalog code out of an error envelope.
 *
 * Returns null when the body is not an error envelope at all — a proxy's HTML error page, a network
 * failure, a truncated response. Null is NOT folded into `unknown`: "we could not find that code"
 * and "we could not reach the server" are different things to tell somebody, and saying the first
 * when the second happened sends them off to re-type a code that was always right.
 */
function codeOf(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const error = (payload as { error?: unknown }).error;
  if (!error || typeof error !== 'object') return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/** The outcome of either call: a value, a problem the person can act on, or an unreachable server. */
export type DeviceAuthResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly problem: ApprovalProblem }
  | { readonly ok: false; readonly unreachable: true };

async function post<T>(
  path: string,
  body: Record<string, unknown>,
  fetchImpl: typeof fetch,
  read: (data: Record<string, unknown>) => T,
): Promise<DeviceAuthResult<T>> {
  let res: Response;
  try {
    res = await fetchImpl(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, unreachable: true };
  }

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    // A response we cannot parse is unreachable-shaped, not a refusal: there is no code in it, so
    // there is nothing honest to tell the person about their grant.
    return { ok: false, unreachable: true };
  }

  // Envelope-first, HTTP status second — the order the rest of the host already uses
  // (`record-write.ts` branches on `env.ok`, not on `res.ok`). A refusal is identified by its CODE
  // wherever that code appears, so a proxy that rewrote the status line cannot turn a legible
  // refusal into an unreachable server.
  const env = payload as { ok?: boolean; data?: Record<string, unknown> } | null;
  const code = codeOf(payload);
  if (code) {
    const problem = PROBLEM_BY_CODE[code];
    // A code we do not recognise is NOT a problem the person can act on — it is one of ours. Saying
    // "we could not find that code" about, say, a permission failure would send somebody off
    // re-typing a code that was never the issue.
    return problem ? { ok: false, problem } : { ok: false, unreachable: true };
  }
  if (env?.ok === false || !res.ok) return { ok: false, unreachable: true };

  return { ok: true, value: read(env?.data ?? {}) };
}

/** Ask what the code refers to, so the screen can name the client before anyone approves it. */
export async function fetchPendingGrant(
  userCode: string,
  fetchImpl: typeof fetch = fetch,
): Promise<DeviceAuthResult<PendingGrantPayload>> {
  return post(DEVICE_PENDING_PATH, { userCode }, fetchImpl, (data) => ({
    clientName: String(data['clientName'] ?? ''),
    requestedAt: String(data['requestedAt'] ?? ''),
    expiresAt: String(data['expiresAt'] ?? ''),
  }));
}

/**
 * Answer the grant.
 *
 * `approve` is always sent explicitly — the kernel refuses a request that omits it, and that
 * refusal is correct: an approval inferred from a missing field is an approval nobody gave.
 */
export async function answerDeviceGrant(
  userCode: string,
  approve: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<DeviceAuthResult<{ readonly approved: boolean; readonly clientName: string }>> {
  return post(DEVICE_APPROVE_PATH, { userCode, approve }, fetchImpl, (data) => ({
    approved: data['approved'] === true,
    clientName: String(data['clientName'] ?? ''),
  }));
}
